import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/features/auth/store/authStore';
import { toast } from 'sonner';
import { getCSRFToken } from '../security';

// API URL: in production build always same-origin (empty) unless VITE_API_URL set. In dev use localhost when not set.
export const API_BASE_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_API_URL || '')
  : (import.meta.env.VITE_API_URL ?? 'http://localhost:5000');

// Create axios instance with secure defaults
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: true, // Send cookies for CORS requests
});

// Request interceptor (add security headers and auth token)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add CSRF token for state-changing requests
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
      config.headers['X-CSRF-Token'] = getCSRFToken();
    }
    
    // Add request timestamp for debugging
    config.headers['X-Request-Time'] = new Date().toISOString();
    
    // For admin API, read token from localStorage first (store may not have rehydrated yet)
    const isAdminRequest = (config.url ?? '').includes('/api/admin') || (config.baseURL ?? '').includes('admin');
    let token: string | null = null;
    if (isAdminRequest) {
      try {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const parsed = JSON.parse(stored);
          token = parsed?.state?.token ?? null;
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (!token) token = useAuthStore.getState().token;
    if (!token) {
      try {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const parsed = JSON.parse(stored);
          token = parsed?.state?.token ?? null;
        }
      } catch (e) {
        console.error('apiClient: Error reading token from localStorage:', e);
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Don't log in production
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor (handle errors securely)
apiClient.interceptors.response.use(
  (response) => {
    // Log rate limit headers if present
    const rateLimitRemaining = response.headers['x-ratelimit-remaining'];
    if (rateLimitRemaining && parseInt(rateLimitRemaining) < 10) {
      console.warn(`[API] Rate limit warning: ${rateLimitRemaining} requests remaining`);
    }
    
    return response;
  },
  async (error: AxiosError) => {
    // Network error (no response from server)
    if (!error.response) {
      console.error('Network Error:', {
        message: error.message,
        apiUrl: API_BASE_URL,
        code: error.code,
      });
      
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        const showUrl = API_BASE_URL && !API_BASE_URL.includes('localhost');
        const msg = showUrl ? `خطأ في الاتصال بالخادم. تأكد أن Backend يعمل على ${API_BASE_URL}` : 'خطأ في الاتصال بالخادم. تأكد أن الخادم يعمل.';
        toast.error(msg, {
          duration: 5000,
        });
      } else if (error.code === 'ECONNABORTED') {
        toast.error('انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.', {
          duration: 5000,
        });
      }
      return Promise.reject(error);
    }

    const status = error.response.status;
    const currentPath = window.location.pathname;
    const isOnLoginPage = currentPath === '/login' || currentPath === '/admin/login' || currentPath === '/register';
    const isOnPublicPage = currentPath === '/' || currentPath === '/contact';

    // Handle specific HTTP errors
    switch (status) {
      case 401:
        // Unauthorized - session expired or invalid token
        if (!isOnLoginPage && !isOnPublicPage) {
          // Don't auto-logout immediately, let the page handle it
          toast.error('يرجى تسجيل الدخول مرة أخرى.', { duration: 3000 });
        }
        break;
        
      case 403:
        // Forbidden - no permission
        toast.error('ليس لديك صلاحية لتنفيذ هذا الإجراء.');
        break;
        
      case 429:
        // Rate limited
        const retryAfter = error.response.headers['retry-after'] || '60';
        toast.error(`تم تجاوز الحد الأقصى للطلبات. يرجى الانتظار ${retryAfter} ثانية.`, {
          duration: parseInt(retryAfter) * 1000,
        });
        break;
        
      case 400:
        // Bad request - show server message if available
        const errorMessage = (error.response.data as any)?.message;
        if (errorMessage) {
          toast.error(errorMessage);
        }
        break;
        
      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors
        toast.error('خطأ في الخادم. يرجى المحاولة لاحقاً.');
        break;
    }
    
    return Promise.reject(error);
  }
);

// Helper function to check if the API is reachable
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await axios.get(`${API_BASE_URL || ''}/health`, { timeout: 5000 });
    return response.status === 200;
  } catch {
    return false;
  }
}

// Export the base URL for direct use
export const getApiUrl = () => API_BASE_URL;

/**
 * Returns full image URL for display. Works across devices (use VITE_API_URL in .env).
 * - If url is empty/undefined, returns undefined.
 * - If url already starts with http(s), returns as-is.
 * - Otherwise prepends API base URL so images work from any machine.
 */
export const getFullImageUrl = (url: string | undefined | null): string | undefined => {
  if (!url || typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const base = (API_BASE_URL || '').replace(/\/$/, '');
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
};
